import { useState } from 'react';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconCheckCircle, IconEdit, IconTrash } from '../../../common/icons';
import { formatDateOnly } from '../../../common/utils/date';
import { PREG_STATE } from '../../../core/constants/dx';
import { useAssignTicket, useDeleteTicket } from '../contexts/asocebu';

// Fila del worklist: un CRUCE (clivage) con sus transferencias y el control del
// ticket ASOCEBU. Asignar/editar = POST find-or-update por clivage; quitar = DELETE.
// El control de escritura está gateado por `asocebu:write`; lectores ven el ticket.

const today = () => new Date().toLocaleDateString('en-CA');

// Nombre prominente, código (número/registro) subordinado y atenuado. Si no hay
// nombre, el código pasa a ser el rótulo principal (sin duplicarlo como subtítulo).
function crossParts(cross) {
    const d = cross?.donor;
    const s = cross?.sire;
    return {
        donorName: d?.name || d?.number || '—',
        donorSub:  d?.name ? (d?.number || '') : '',
        sireName:  s?.name || s?.register || '—',
        sireSub:   s?.name ? (s?.register || '') : '',
    };
}

export default function CrossRow({ item, transfers }) {
    // En la vista agrupada (OPU → T.E.), `transfers` son las reportables de ESTA T.E.;
    // sin agrupar, cae a todas las del cruce. El ticket/cruce siguen saliendo de `item`.
    const rows = transfers || item.transfers || [];
    const { mutateAsync: assign, isPending: assigning } = useAssignTicket();
    const { mutateAsync: remove, isPending: removing } = useDeleteTicket();

    const has = !!item.ticket;
    const [editing, setEditing] = useState(false);
    const [ticket, setTicket] = useState(item.ticket?.ticket || '');
    const [date, setDate] = useState(item.ticket?.requested_at || today());

    const open = !has || editing; // formulario visible
    const busy = assigning || removing;
    const { donorName, donorSub, sireName, sireSub } = crossParts(item.cross);

    const startEdit = () => {
        setTicket(item.ticket?.ticket || '');
        setDate(item.ticket?.requested_at || today());
        setEditing(true);
    };

    const save = async () => {
        const id = ticket.trim();
        if (!id) { toast.error('Ingresá el ID de ASOCEBU'); return; }
        try {
            await assign({ clivage_id: item.clivage_id, ticket: id, ...(date ? { requested_at: date } : {}) });
            toast.success(has ? 'Ticket actualizado' : 'Ticket asignado');
            setEditing(false);
        } catch (err) {
            if (err?.status === 409) toast.error('El cruce no existe o es externo (sin clivage)');
            else if (err?.status !== 403) toast.error(err?.message || 'No se pudo guardar el ticket');
        }
    };

    const del = async () => {
        if (!item.ticket?.id) return;
        try {
            await remove(item.ticket.id);
            toast.success('Ticket quitado');
            // tras el delete el cruce queda sin ticket y vuelve el form de asignar:
            // limpiar el estado local para no precargar el valor recién borrado.
            setEditing(false);
            setTicket('');
            setDate(today());
        } catch (err) {
            if (err?.status !== 403) toast.error(err?.message || 'No se pudo quitar el ticket');
        }
    };

    return (
        <article className="aso-cross">
            <div className="aso-cross__genetics">
                <div className="aso-parent aso-parent--donor">
                    <span className="aso-parent__role">Donante</span>
                    <span className="aso-parent__name" title={donorName}>{donorName}</span>
                    {donorSub && <span className="aso-parent__code" title={donorSub}>{donorSub}</span>}
                </div>
                <span className="aso-cross__x" aria-hidden="true">×</span>
                <div className="aso-parent aso-parent--sire">
                    <span className="aso-parent__role">Toro</span>
                    <span className="aso-parent__name" title={sireName}>{sireName}</span>
                    {sireSub && <span className="aso-parent__code" title={sireSub}>{sireSub}</span>}
                </div>
            </div>

            <div className="aso-cross__recips">
                <span className="aso-cross__recips-label">Receptora{rows.length !== 1 ? 's' : ''}</span>
                <ul className="aso-recips">
                    {rows.map((t) => {
                        const st = PREG_STATE[t.pregnancy?.state];
                        return (
                            <li key={t.id} className="aso-recip">
                                <span className="aso-recip__num" title={t.recipient?.number || ''}>{t.recipient?.number || '—'}</span>
                                {t.recipient?.name && <span className="aso-recip__name">{t.recipient.name}</span>}
                                {st && <span className={`aso-preg aso-preg--${st.tone}`}>{st.label}</span>}
                            </li>
                        );
                    })}
                    {!rows.length && <li className="aso-recip aso-recip--none">Sin transferencias</li>}
                </ul>
            </div>

            <div className="aso-cross__ticket">
                {has && !editing && (
                    <div className="aso-ticket">
                        <span className="aso-ticket__badge"><IconCheckCircle /> {item.ticket.ticket}</span>
                        {item.ticket.requested_at && <span className="aso-ticket__date">Solicitado {formatDateOnly(item.ticket.requested_at)}</span>}
                        <Gate domain="asocebu" action="write">
                            <div className="aso-ticket__actions">
                                <button type="button" className="aso-icon-btn" onClick={startEdit} disabled={busy} aria-label="Editar ticket" title="Editar"><IconEdit /></button>
                                <button type="button" className="aso-icon-btn aso-icon-btn--danger" onClick={del} disabled={busy} aria-label="Quitar ticket" title="Quitar"><IconTrash /></button>
                            </div>
                        </Gate>
                    </div>
                )}

                {open && (
                    <Gate domain="asocebu" action="write" fallback={<span className="aso-ticket__none">Sin ticket</span>}>
                        <div className="aso-ticket-form">
                            <input type="text" className="aso-ticket-form__id" value={ticket} onChange={(e) => setTicket(e.target.value)} maxLength={80} placeholder="ID ASOCEBU" disabled={busy} />
                            <input type="date" className="aso-ticket-form__date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} disabled={busy} />
                            <div className="aso-ticket-form__actions">
                                {editing && <button type="button" className="aso-btn aso-btn--ghost" onClick={() => setEditing(false)} disabled={busy}>Cancelar</button>}
                                <button type="button" className="aso-btn aso-btn--primary" onClick={save} disabled={busy || !ticket.trim()}>
                                    {busy ? 'Guardando…' : (has ? 'Guardar' : 'Asignar')}
                                </button>
                            </div>
                        </div>
                    </Gate>
                )}
            </div>
        </article>
    );
}
